import { useState, useEffect, useCallback } from "react";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

const sb = (path, opts = {}) =>
  fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      "Content-Type": "application/json",
      Prefer: "return=representation",
      ...opts.headers,
    },
    ...opts,
  }).then((r) => r.json());

const fnCall = (slug, body) =>
  fetch(`${SUPABASE_URL}/functions/v1/${slug}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify(body),
  }).then((r) => r.json());

const STATUS_COLOR = {
  pending: "#6b7280",
  contacted: "#3b82f6",
  interested: "#10b981",
  not_interested: "#ef4444",
  paused: "#f59e0b",
};

const STATUS_LABEL = {
  pending: "Da contattare",
  contacted: "Contattato",
  interested: "Interessato",
  not_interested: "Non interessato",
  paused: "In pausa",
};

function Badge({ status }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 0.5,
        background: (STATUS_COLOR[status] || "#6b7280") + "22",
        color: STATUS_COLOR[status] || "#6b7280",
        border: `1px solid ${STATUS_COLOR[status] || "#6b7280"}44`,
      }}
    >
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div
      style={{
        background: "#0f1117",
        border: "1px solid #1e2230",
        borderRadius: 8,
        padding: "16px 20px",
        minWidth: 120,
      }}
    >
      <div style={{ fontSize: 11, color: "#6b7280", letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 28, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: color || "#e5e7eb" }}>
        {value}
      </div>
    </div>
  );
}

export default function SponsorCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [limitAll, setLimitAll] = useState(false);

  const loadCampaigns = useCallback(async () => {
    setLoadingCampaigns(true);
    const data = await sb("volta_sponsor_campaigns?select=*&order=created_at.desc");
    setCampaigns(Array.isArray(data) ? data : []);
    setLoadingCampaigns(false);
  }, []);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const loadLeads = useCallback(async (campaignId) => {
    setLoadingLeads(true);
    setLeads([]);
    setSendResult(null);
    const data = await sb(
      `volta_sponsor_leads?campaign_id=eq.${campaignId}&order=created_at.asc&limit=200`
    );
    setLeads(Array.isArray(data) ? data : []);
    setLoadingLeads(false);
  }, []);

  const selectCampaign = (c) => {
    setSelected(c);
    loadLeads(c.id);
  };

  const handleSend = async () => {
    if (!selected) return;
    setSending(true);
    setSendResult(null);
    try {
      const body = {
        campaign_id: selected.id,
        dry_run: dryRun,
      };
      if (!limitAll) body.limit = 10;
      const result = await fnCall("sponsor-outreach", body);
      setSendResult(result);
      if (!dryRun) {
        await loadCampaigns();
        await loadLeads(selected.id);
      }
    } catch (e) {
      setSendResult({ error: String(e) });
    }
    setSending(false);
  };

  const pendingLeads = leads.filter((l) => !l.email_sent_at);
  const contactedLeads = leads.filter((l) => l.email_sent_at);
  const interestedLeads = leads.filter((l) => l.status === "interested");

  return (
    <div style={{ padding: "32px 40px", color: "#e5e7eb", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: "#f9fafb", margin: 0 }}>
          Campagne Sponsor
        </h1>
        <p style={{ color: "#6b7280", marginTop: 4, fontSize: 14 }}>
          Gestisci le campagne di outreach sponsor e invia le email direttamente da qui.
        </p>
      </div>

      <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
        {/* Lista campagne */}
        <div style={{ width: 300, flexShrink: 0 }}>
          <div style={{ fontSize: 11, color: "#6b7280", letterSpacing: 1, textTransform: "uppercase", marginBottom: 12 }}>
            Campagne attive
          </div>
          {loadingCampaigns ? (
            <div style={{ color: "#6b7280", fontSize: 14 }}>Caricamento...</div>
          ) : campaigns.length === 0 ? (
            <div style={{ color: "#6b7280", fontSize: 14 }}>Nessuna campagna trovata.</div>
          ) : (
            campaigns.map((c) => (
              <div
                key={c.id}
                onClick={() => selectCampaign(c)}
                style={{
                  background: selected?.id === c.id ? "#1e2230" : "#0f1117",
                  border: `1px solid ${selected?.id === c.id ? "#3b82f6" : "#1e2230"}`,
                  borderRadius: 8,
                  padding: "14px 16px",
                  marginBottom: 8,
                  cursor: "pointer",
                  transition: "border-color 0.15s",
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 14, color: "#f9fafb", marginBottom: 4 }}>
                  {c.name}
                </div>
                <div style={{ display: "flex", gap: 12, fontSize: 12, color: "#9ca3af" }}>
                  <span
                    style={{
                      display: "inline-block",
                      padding: "1px 7px",
                      borderRadius: 4,
                      fontSize: 11,
                      background: c.status === "active" ? "#10b98122" : "#6b728022",
                      color: c.status === "active" ? "#10b981" : "#6b7280",
                      border: `1px solid ${c.status === "active" ? "#10b98144" : "#6b728044"}`,
                    }}
                  >
                    {c.status}
                  </span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    {c.total_emails_sent ?? 0} inviati
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Dettaglio campagna */}
        {selected ? (
          <div style={{ flex: 1 }}>
            {/* Stats */}
            <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
              <StatCard label="Lead totali" value={leads.length} />
              <StatCard label="Da contattare" value={pendingLeads.length} color="#e5e7eb" />
              <StatCard label="Contattati" value={contactedLeads.length} color="#3b82f6" />
              <StatCard label="Interessati" value={interestedLeads.length} color="#10b981" />
            </div>

            {/* Pannello invio */}
            <div
              style={{
                background: "#0f1117",
                border: "1px solid #1e2230",
                borderRadius: 8,
                padding: "20px 24px",
                marginBottom: 24,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: "#f9fafb", marginBottom: 16 }}>
                Invia outreach
              </div>

              <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                {/* Dry run toggle */}
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#9ca3af", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={dryRun}
                    onChange={(e) => setDryRun(e.target.checked)}
                    style={{ accentColor: "#f59e0b", width: 14, height: 14 }}
                  />
                  Dry run (preview, non invia)
                </label>

                {/* Limit toggle */}
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#9ca3af", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={limitAll}
                    onChange={(e) => setLimitAll(e.target.checked)}
                    style={{ accentColor: "#f59e0b", width: 14, height: 14 }}
                  />
                  Invia tutti ({pendingLeads.length} da contattare)
                </label>

                <button
                  onClick={handleSend}
                  disabled={sending || pendingLeads.length === 0}
                  style={{
                    background: dryRun ? "#f59e0b" : "#3b82f6",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    padding: "8px 20px",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: sending || pendingLeads.length === 0 ? "not-allowed" : "pointer",
                    opacity: sending || pendingLeads.length === 0 ? 0.5 : 1,
                    transition: "opacity 0.15s",
                  }}
                >
                  {sending
                    ? "In corso..."
                    : dryRun
                    ? `Preview ${limitAll ? "tutti" : "10"}`
                    : `Invia ${limitAll ? "tutti" : "10"}`}
                </button>

                <button
                  onClick={() => loadLeads(selected.id)}
                  style={{
                    background: "transparent",
                    color: "#6b7280",
                    border: "1px solid #1e2230",
                    borderRadius: 6,
                    padding: "8px 14px",
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  ↻ Ricarica
                </button>
              </div>

              {/* Risultato invio */}
              {sendResult && (
                <div
                  style={{
                    marginTop: 16,
                    padding: "12px 16px",
                    borderRadius: 6,
                    background: sendResult.error ? "#ef444411" : "#10b98111",
                    border: `1px solid ${sendResult.error ? "#ef444444" : "#10b98144"}`,
                    fontSize: 13,
                  }}
                >
                  {sendResult.error ? (
                    <div style={{ color: "#ef4444" }}>Errore: {sendResult.error}</div>
                  ) : (
                    <div>
                      <div style={{ color: "#10b981", fontWeight: 600, marginBottom: 8 }}>
                        {sendResult.dry_run ? "Preview completato" : "Invio completato"}
                      </div>
                      <div style={{ color: "#9ca3af", display: "flex", gap: 20, flexWrap: "wrap" }}>
                        {sendResult.dry_run ? (
                          <>
                            <span>Preview: <strong style={{ color: "#e5e7eb" }}>{sendResult.would_send ?? 0}</strong></span>
                            <span>Template: <strong style={{ color: "#e5e7eb" }}>{sendResult.template_name || "—"}</strong></span>
                          </>
                        ) : (
                          <>
                            <span>Inviati: <strong style={{ color: "#e5e7eb" }}>{sendResult.sent ?? 0}</strong></span>
                            <span>Saltati (invalid): <strong style={{ color: "#e5e7eb" }}>{sendResult.skipped_invalid ?? 0}</strong></span>
                            <span>Saltati (blocklist): <strong style={{ color: "#e5e7eb" }}>{sendResult.skipped_blocklisted ?? 0}</strong></span>
                            <span>Falliti: <strong style={{ color: "#e5e7eb" }}>{sendResult.failed ?? 0}</strong></span>
                          </>
                        )}
                      </div>
                      {/* Preview emails in dry run */}
                      {sendResult.dry_run && sendResult.previews?.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>Preview email:</div>
                          {sendResult.previews.map((p, i) => (
                            <div
                              key={i}
                              style={{
                                background: "#070b12",
                                border: "1px solid #1e2230",
                                borderRadius: 6,
                                padding: "10px 14px",
                                marginBottom: 8,
                                fontSize: 12,
                              }}
                            >
                              <div style={{ fontWeight: 600, color: "#e5e7eb", marginBottom: 4 }}>
                                {p.company_name || p.to}
                              </div>
                              <div style={{ color: "#6b7280" }}>
                                A: {p.to} — Oggetto: {p.subject}
                              </div>
                              <div style={{ color: "#4b5563", marginTop: 4, fontFamily: "monospace", fontSize: 11 }}>
                                {p.body_bytes} bytes · template: {p.used_template ? "✓" : "✗ (Claude)"}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Tabella lead */}
            <div
              style={{
                background: "#0f1117",
                border: "1px solid #1e2230",
                borderRadius: 8,
                overflow: "hidden",
              }}
            >
              <div style={{ padding: "16px 20px", borderBottom: "1px solid #1e2230", fontSize: 13, fontWeight: 600, color: "#f9fafb" }}>
                Lead ({leads.length})
              </div>
              {loadingLeads ? (
                <div style={{ padding: 24, color: "#6b7280", fontSize: 14 }}>Caricamento lead...</div>
              ) : leads.length === 0 ? (
                <div style={{ padding: 24, color: "#6b7280", fontSize: 14 }}>Nessun lead in questa campagna.</div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #1e2230" }}>
                      {["Azienda", "Email", "Settore", "Stato", "Inviato"].map((h) => (
                        <th
                          key={h}
                          style={{
                            textAlign: "left",
                            padding: "10px 16px",
                            color: "#6b7280",
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: 0.5,
                            textTransform: "uppercase",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead, i) => (
                      <tr
                        key={lead.id}
                        style={{
                          borderBottom: i < leads.length - 1 ? "1px solid #0d111a" : "none",
                          background: i % 2 === 0 ? "transparent" : "#070b1233",
                        }}
                      >
                        <td style={{ padding: "10px 16px", color: "#f9fafb", fontWeight: 500 }}>
                          {lead.company_name || "—"}
                          {lead.contact_name && (
                            <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{lead.contact_name}</div>
                          )}
                        </td>
                        <td style={{ padding: "10px 16px", color: "#9ca3af", fontFamily: "monospace", fontSize: 12 }}>
                          {lead.contact_email || "—"}
                        </td>
                        <td style={{ padding: "10px 16px", color: "#6b7280", fontSize: 12 }}>
                          {lead.sector || "—"}
                        </td>
                        <td style={{ padding: "10px 16px" }}>
                          <Badge status={lead.status || "pending"} />
                        </td>
                        <td style={{ padding: "10px 16px", color: "#6b7280", fontSize: 12, fontFamily: "monospace" }}>
                          {lead.email_sent_at
                            ? new Date(lead.email_sent_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        ) : (
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#374151",
              fontSize: 14,
              minHeight: 300,
              border: "1px dashed #1e2230",
              borderRadius: 8,
            }}
          >
            Seleziona una campagna dalla lista per vedere i lead e inviare l'outreach.
          </div>
        )}
      </div>
    </div>
  );
}
